/**
 * bimGarageDoorParts — the sectional garage door's parametric geometry, ported
 * from the house-ifc Python module (`devices/garage-door/garage_door.py`).
 *
 * The module builds the door as ~86 IFC solids in the IFC frame; this is the
 * same door as part boxes, for the app's live device layer. Every number below
 * is the Python module's own constant, converted mm → m.
 *
 * ── The two frames, and the conversion (why the signs move) ─────────────────
 *
 * The MODULE's frame (its own contract, and the frame the canvas element
 * `#468608` was authored in):
 *
 *   · origin  — the OUTER face of the leaf, mid-width, at FLOOR level;
 *   · +X      — across the width;  +Y — INTO the opening (interior);
 *   · +Z      — up from the floor;  the leaf occupies y ∈ [0, LEAF_T].
 *
 * The DEVICE-MODEL frame (`bimDeviceModel.shadePartsInDeviceFrame`, card
 * t_ad566d07) that the renderer places on the widget's pin:
 *
 *   · +X — across the width;  +Y — UP;  +Z — OUT of the plane (the outward
 *     normal);
 *   · origin — the CENTRE OF THE FULLY-EXPANDED product, i.e. mid-width,
 *     mid-height of the closed door, ON the door's own plane.
 *
 * So a module box at (x, y, z) with sizes (dx, dy, dz) lands here:
 *
 *   · `y_dev = ref + z`  (ref = the leaf's bottom edge for a part that travels
 *     with the leaf, `−fullHeight/2` for a part fixed to the opening);
 *   · `z_dev = −PIN_STANDOFF − y`  (`widgets.py` anchors the pin 20 mm proud of
 *     the surface, so the door's own plane sits at −20 mm and every module depth
 *     is measured inward from there: the leaf at [−62, −20] mm, the proud panel
 *     joints at [−20, −10] mm, the jet-black surround arriving exactly at the
 *     pin plane [−20, 0] mm, the handle 6 mm through it).
 *
 * `bimGarageDoorParts.test.ts` pins the numbers against the Python module's own
 * output — the same discipline `bimShadeParts.test.ts` uses.
 *
 * ── The live state ──────────────────────────────────────────────────────────
 *
 * `dropM` is how much door is HANGING: the app's cover plumbing feeds a cover's
 * `position` (0 = up, 100 = fully down) into the drop, and a garage door's own
 * `position` reads the same way (100 = closed). The leaf's TOP edge stays at the
 * lintel and it shortens upward as the door opens, so a fully closed door spans
 * the whole opening and a fully open one keeps a stub at the lintel — the same
 * floor `MIN_DEVICE_MODEL_DROP_MM` gives a rolled-up shade. The surround and the
 * running gear are fixed to the opening; the seal and the handle ride the leaf.
 */

import type { Vec3 } from './bimShadeSpec';

/** Which shared material a part uses (the module's own style keys). */
export type GarageDoorPartMaterial =
  | 'leaf'
  | 'joint'
  | 'frame'
  | 'shadow'
  | 'seal'
  | 'steel'
  | 'hardware';

export interface GarageDoorPartBox {
  /** Stable slot key across states: 'leaf-<i>' | 'mullion-<i>-<j>' | 'jamb-l' | … */
  key: string;
  material: GarageDoorPartMaterial;
  /** Box centre, metres, in the DEVICE-MODEL frame above. */
  center: Vec3;
  /** Full box size, metres: [X = width, Y = up, Z = out]. */
  size: Vec3;
}

const MM = 0.001;

/** The module's constants (`devices/garage-door/garage_door.py`, mm → m). */
export const GARAGE_DOOR = {
  /** `LEAF_T` — insulated leaf thickness (the visible plane is the leaf's outer face). */
  leafT: 42 * MM,
  /** `ROW_GAP` — the shadow joint between two sections. */
  rowGap: 6 * MM,
  /** `MULL_W` — vertical panel joint width. */
  mullW: 22 * MM,
  /** `STILE_H` — horizontal panel joint height. */
  stileH: 22 * MM,
  /** `PANEL_PROUD` — how far the joints stand in front of the leaf face. */
  panelProud: 10 * MM,
  /** `JAMB_W` / `JAMB_PROUD` — the slim jet-black aluminium surround. */
  jambW: 70 * MM,
  jambProud: 20 * MM,
  /** `SEAL_H` / `SEAL_PROUD` — the EPDM bottom seal. */
  sealH: 45 * MM,
  sealProud: 6 * MM,
  /** `HANDLE_X` / `HANDLE_PROUD` — the bar handle, from the door's centre. */
  handleX: 1500 * MM,
  handleProud: 26 * MM,
  /** The joint cavity behind a section boundary (`box(…, LEAF_T, …, 6.0, ROW_GAP)`). */
  cavityD: 6 * MM,
  /** Running gear (`TRACK_*` / `RAIL_*` / `SHAFT_*` / `SPRING_*` / `DRUM_*`). */
  trackW: 60 * MM,
  trackY0: 430 * MM,
  trackD: 60 * MM,
  /** `TRACK_Z1` — the vertical track's top, as the module's rise over the door's own height. */
  trackRise: 50 * MM,
  /** `RAIL_Y1` — how far the low-headroom rail runs into the garage. */
  railY1: 2400 * MM,
  /** `RAIL_Z` — the rail's underside, again as a rise over the door's height. */
  railRise: 140 * MM,
  railT: 40 * MM,
  /** `SHAFT_Z` — the torsion shaft's axis. */
  shaftRise: 100 * MM,
  shaftY: 490 * MM,
  shaftR: 16 * MM,
  springR: 45 * MM,
  springL: 900 * MM,
  drumR: 55 * MM,
  drumL: 90 * MM,
  /** `widgets.py`'s standoff: the pin sits this far proud of the surface. */
  pinStandoff: 20 * MM,
  /** The door's sections and the panels across it (the schema's `prefs`). */
  rows: 5,
  cols: 8,
  /** The panel grid a family can be asked for (the schema's own range). */
  minRows: 3,
  maxRows: 8,
  minCols: 2,
  maxCols: 14,
} as const;

/** Rows/cols clamped into the range above, so a stored typo cannot run away. */
export function garageDoorGrid(rows: number | undefined, cols: number | undefined): {
  rows: number;
  cols: number;
} {
  const clamp = (value: number | undefined, min: number, max: number, fallback: number): number => {
    const n = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : fallback;
    return Math.min(max, Math.max(min, n));
  };
  return {
    rows: clamp(rows, GARAGE_DOOR.minRows, GARAGE_DOOR.maxRows, GARAGE_DOOR.rows),
    cols: clamp(cols, GARAGE_DOOR.minCols, GARAGE_DOOR.maxCols, GARAGE_DOOR.cols),
  };
}

export interface GarageDoorParams {
  widthM: number;
  /** How much door hangs right now (the live state's drop). */
  dropM: number;
  /** The frame's own full height — the opening, not the state. */
  dropFullM: number;
  prefs: { rows?: number; cols?: number };
}

/**
 * The door's part boxes at one size and state.
 *
 * `dropM ≥ dropFullM` is a closed door (every row at its full share); a smaller
 * drop lifts the bottom edge, the rows keeping their proportion, exactly as the
 * module's own `row_h = (h − (rows−1)·ROW_GAP) / rows` does for a shorter leaf.
 */
export function garageDoorParts(params: GarageDoorParams): GarageDoorPartBox[] {
  const w = Math.max(0.3, params.widthM);
  const full = Math.max(0.3, params.dropFullM);
  const drop = Math.min(full, Math.max(0.15, params.dropM));
  const { rows, cols } = garageDoorGrid(params.prefs.rows, params.prefs.cols);
  const out: GarageDoorPartBox[] = [];

  /** The opening's own floor line (and the lintel), in the device frame. */
  const bottomFull = -full / 2;
  /** Where the leaf's bottom edge is now — a part that rides the leaf uses this. */
  const leafBottom = full / 2 - drop;
  /** The leaf's TOP edge: the lintel, which never moves. */
  const leafTop = full / 2;

  /**
   * One module box → one device-frame part.
   *
   * `x0` is from the door's centre; `y0` is the depth into the opening (the
   * module's +Y); `z0` is the height above `ref`; the sizes are the module's own.
   */
  const box = (
    key: string,
    material: GarageDoorPartMaterial,
    x0: number,
    y0: number,
    z0: number,
    dx: number,
    dy: number,
    dz: number,
    ref: number,
  ): void => {
    if (dx <= 0 || dy <= 0 || dz <= 0) return;
    out.push({
      key,
      material,
      center: [x0 + dx / 2, ref + z0 + dz / 2, -GARAGE_DOOR.pinStandoff - y0 - dy / 2],
      size: [dx, dz, dy],
    });
  };

  // ── leaf: `rows` sections, a ROW_GAP shadow joint between them ────────────
  const rowH = (drop - (rows - 1) * GARAGE_DOOR.rowGap) / rows;
  for (let i = 0; i < rows; i += 1) {
    const z0 = i * (rowH + GARAGE_DOOR.rowGap);
    box(`leaf-${i}`, 'leaf', -w / 2, 0, z0, w, GARAGE_DOOR.leafT, rowH, leafBottom);
    if (i > 0) {
      // The cavity behind the section boundary (it reads as a shadow line).
      box(
        `shadow-${i}`,
        'shadow',
        -w / 2,
        GARAGE_DOOR.leafT,
        z0 - GARAGE_DOOR.rowGap,
        w,
        GARAGE_DOOR.cavityD,
        GARAGE_DOOR.rowGap,
        leafBottom,
      );
    }
    // This section's panel joints: the vertical mullions (the first/last one is
    // clamped to the door's own width, so nothing crosses into the reveal) …
    for (let j = 0; j <= cols; j += 1) {
      const xc = -w / 2 + (j * w) / cols;
      const x0 = Math.max(-w / 2, xc - GARAGE_DOOR.mullW / 2);
      const x1 = Math.min(w / 2, xc + GARAGE_DOOR.mullW / 2);
      box(
        `mullion-${i}-${j}`,
        'joint',
        x0,
        -GARAGE_DOOR.panelProud,
        z0,
        x1 - x0,
        GARAGE_DOOR.panelProud,
        rowH,
        leafBottom,
      );
    }
    // … and the top/bottom stiles.
    box(
      `stile-top-${i}`,
      'joint',
      -w / 2,
      -GARAGE_DOOR.panelProud * 0.8,
      z0,
      w,
      GARAGE_DOOR.panelProud * 0.8,
      GARAGE_DOOR.stileH,
      leafBottom,
    );
    box(
      `stile-bottom-${i}`,
      'joint',
      -w / 2,
      -GARAGE_DOOR.panelProud * 0.8,
      z0 + rowH - GARAGE_DOOR.stileH,
      w,
      GARAGE_DOOR.panelProud * 0.8,
      GARAGE_DOOR.stileH,
      leafBottom,
    );
  }
  void leafTop;

  // ── the slim jet-black surround: two jambs, fixed to the opening ──────────
  for (const sx of [-1, 1]) {
    const x0 = sx < 0 ? -w / 2 : w / 2 - GARAGE_DOOR.jambW;
    box(
      sx < 0 ? 'jamb-l' : 'jamb-r',
      'frame',
      x0,
      -GARAGE_DOOR.jambProud,
      0,
      GARAGE_DOOR.jambW,
      GARAGE_DOOR.jambProud,
      full,
      bottomFull,
    );
  }

  // ── the bottom seal: it rides the leaf's bottom edge ─────────────────────
  box('seal', 'seal', -w / 2, -GARAGE_DOOR.sealProud, 0, w, GARAGE_DOOR.sealProud, GARAGE_DOOR.sealH, leafBottom);

  // ── handle + lock, on the weather face of the leaf ───────────────────────
  box(
    'handle-bar',
    'hardware',
    GARAGE_DOOR.handleX - 15 * MM,
    -GARAGE_DOOR.handleProud,
    1020 * MM,
    30 * MM,
    GARAGE_DOOR.handleProud,
    260 * MM,
    leafBottom,
  );
  box('handle-lock', 'hardware', GARAGE_DOOR.handleX - 40 * MM, -14 * MM, 760 * MM, 80 * MM, 14 * MM, 120 * MM, leafBottom);
  box(
    'handle-cylinder',
    'hardware',
    GARAGE_DOOR.handleX - 12 * MM,
    -14 * MM,
    800 * MM,
    24 * MM,
    10 * MM,
    40 * MM,
    leafBottom,
  );

  // ── the running gear behind the leaf, fixed to the opening ───────────────
  // The garage's ceiling is an AB slab whose underside sits ~2.6 m up (measured
  // on the house: `tools/gd-ceiling.py`), so the module's layout is a
  // LOW-HEADROOM one and every gear part is expressed as a rise over the door's
  // own height (`TRACK_Z1 = h + 50`, `RAIL_Z = h + 140`, `SHAFT_Z = h + 100`) —
  // a taller door carries its gear higher rather than colliding with the leaf.
  const trackTop = full + GARAGE_DOOR.trackRise;
  const railZ = full + GARAGE_DOOR.railRise;
  const shaftZ = full + GARAGE_DOOR.shaftRise;
  const spring = 45 * MM;
  const springR = GARAGE_DOOR.springR;
  for (const [side, x0] of [
    ['l', -w / 2],
    ['r', w / 2 - GARAGE_DOOR.trackW],
  ] as const) {
    box(
      `track-${side}`,
      'steel',
      x0,
      GARAGE_DOOR.trackY0,
      80 * MM,
      GARAGE_DOOR.trackW,
      GARAGE_DOOR.trackD,
      trackTop - 80 * MM,
      bottomFull,
    );
    // The curved transition: three steps from the vertical track to the rail.
    box(
      `track-step-${side}`,
      'steel',
      x0,
      GARAGE_DOOR.trackY0 + GARAGE_DOOR.trackD,
      trackTop,
      GARAGE_DOOR.trackW,
      70 * MM,
      70 * MM,
      bottomFull,
    );
    box(
      `track-bend-${side}`,
      'steel',
      x0,
      GARAGE_DOOR.trackY0 + GARAGE_DOOR.trackD + 70 * MM,
      trackTop + 45 * MM,
      GARAGE_DOOR.trackW,
      70 * MM,
      60 * MM,
      bottomFull,
    );
    // The horizontal rail — low headroom: it stops well short of the back wall.
    const ry = GARAGE_DOOR.trackY0 + GARAGE_DOOR.trackD + 140 * MM;
    box(
      `rail-${side}`,
      'steel',
      x0,
      ry,
      railZ,
      GARAGE_DOOR.trackW,
      GARAGE_DOOR.railY1 - ry,
      GARAGE_DOOR.railT,
      bottomFull,
    );
    // The side bracket carrying the track and the shaft.
    box(`bracket-${side}`, 'steel', x0, GARAGE_DOOR.trackY0, full, GARAGE_DOOR.trackW, GARAGE_DOOR.trackD, 120 * MM, bottomFull);
  }
  // A torsion shaft across the whole width, two springs, an end drum each side.
  // The module turns these on the lathe (`_prism_x`, 10/12-sided); a box of the
  // same footprint reads identically at the sizes a dashboard shows.
  const sx0 = -w / 2 + GARAGE_DOOR.trackW + 10 * MM;
  const sx1 = w / 2 - GARAGE_DOOR.trackW - 10 * MM;
  box(
    'shaft',
    'steel',
    sx0,
    GARAGE_DOOR.shaftY - GARAGE_DOOR.shaftR,
    shaftZ - GARAGE_DOOR.shaftR,
    sx1 - sx0,
    GARAGE_DOOR.shaftR * 2,
    GARAGE_DOOR.shaftR * 2,
    bottomFull,
  );
  [-(GARAGE_DOOR.springL + 60 * MM), 60 * MM].forEach((cx, i) => {
    box(
      `spring-${i}`,
      'steel',
      cx,
      GARAGE_DOOR.shaftY - springR,
      shaftZ - springR,
      GARAGE_DOOR.springL,
      springR * 2,
      springR * 2,
      bottomFull,
    );
  });
  [sx0, sx1 - GARAGE_DOOR.drumL].forEach((dx, i) => {
    box(
      `drum-${i}`,
      'steel',
      dx,
      GARAGE_DOOR.shaftY + 30 * MM - GARAGE_DOOR.drumR,
      shaftZ - 15 * MM - GARAGE_DOOR.drumR,
      GARAGE_DOOR.drumL,
      GARAGE_DOOR.drumR * 2,
      GARAGE_DOOR.drumR * 2,
      bottomFull,
    );
  });
  void spring;
  // The centre bearing plate on the head wall.
  box('bearing', 'steel', -70 * MM, GARAGE_DOOR.trackY0, shaftZ - 40 * MM, 140 * MM, 40 * MM, 80 * MM, bottomFull);

  return out;
}
